import { over } from 'lodash-es';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { getLcn } from '~/utils/labeled-classnames';

export default function HeroWithBackground({
  bgUrl,
  children,
  overlayColor = 'transparent',
  overlayOpacity = .5
}: {
  bgUrl: string
  children: React.ReactNode
  overlayColor?: string
  overlayOpacity?: number
}) {
  const divRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);

  const styles: Record<string, CSSProperties> = {
    bgContainer: {
      backgroundImage: `url('${bgUrl}')`,
      transform: `translate3d(0px, ${scrollY}px, 0px)`,
    },
    overlay: {
      backgroundColor: overlayColor,
      opacity: overlayOpacity,
    }
  }

  const lcn = getLcn({
    heroWithBackground: [
      'bg-contrast-2 relative',
      '-mx-3 sm:-mx-4' // accounts for padding in StandardLayout
    ],
    bgContainerContainer: 'absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none -z-20',
    bgContainer: 'no-repeat bg-center bg-cover opacity-100 top-auto bottom-0 h-[130%]',
    overlay: 'absolute top-0 left-0 w-full h-full pointer-events-none -z-10 ',
  });

  useEffect(() => {
    const handleScroll = () => {
      if (divRef.current) {
        const rect = divRef.current.getBoundingClientRect();
        const offsetFromTop = rect.top;
        const elementHeight = rect.height;

        // Only transform when element is in or near viewport
        if (offsetFromTop < window.innerHeight && offsetFromTop + elementHeight > 0) {
          const translateY = -offsetFromTop * 0.1;
          setScrollY(translateY);
        }
      }
    };

    handleScroll(); // Initial calculation
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);


  return (
    <div className={lcn.heroWithBackground} ref={divRef} >
      <div className={lcn.overlay} style={styles.overlay} />
      <div className={lcn.bgContainerContainer} >
        <div className={lcn.bgContainer} style={styles.bgContainer} />
      </div>
      {children}
    </div>
  );
}; 